import Keycloak from 'keycloak-js'

import { CLIENT_ID, IDP_HINT, OIDC_DOMAIN, REALM } from '../config/api'

const SSO_SESSION_IDLE_MS = 30 * 60 * 1000
const IDLE_CHECK_INTERVAL_MS = 60000 // check every 1 min

let keycloakInstance: Keycloak | null = null
let initPromise: Promise<boolean> | null = null
let tokenExpirationTimer: NodeJS.Timeout | null = null
let idleCheckInterval: NodeJS.Timeout | null = null
let lastActivityTimestamp = Date.now()
const eventListeners: { type: string; listener: EventListener }[] = []
const isSessionIdle = (): boolean => {
  return Date.now() - lastActivityTimestamp > SSO_SESSION_IDLE_MS
}
const updateLastActivity = () => {
  lastActivityTimestamp = Date.now()
}

const addActivityListener = (type: string, listener: EventListener) => {
  document.addEventListener(type, listener)
  eventListeners.push({ type, listener })
}

const removeActivityListeners = () => {
  eventListeners.forEach(({ type, listener }) => {
    document.removeEventListener(type, listener)
  })
  eventListeners.length = 0
}

const createKeycloak = () => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      clientId: CLIENT_ID,
      realm: REALM,
      url: OIDC_DOMAIN,
    })
  }

  return keycloakInstance
}

const initKeycloak = async (): Promise<boolean> => {
  if (initPromise) {
    return initPromise
  }

  const keycloak = createKeycloak()

  initPromise = keycloak.init({
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri:
      window.location.origin +
      process.env.PUBLIC_URL +
      '/silent-check-sso.html',
    pkceMethod: 'S256',
  })

  try {
    const authenticated = await initPromise

    if (!authenticated) {
      keycloak.login(IDP_HINT ? { idpHint: IDP_HINT } : undefined)

      return false
    }

    if (authenticated && keycloakInstance?.tokenParsed?.exp) {
      const expiresIn =
        keycloakInstance.tokenParsed.exp - Math.floor(Date.now() / 1000)
      setupTokenRefresh(expiresIn)
    }

    if (authenticated) {
      idleCheckInterval = setInterval(() => {
        if (isSessionIdle()) {
          keycloak?.logout()
        }
      }, IDLE_CHECK_INTERVAL_MS)
      addActivityListener('mousemove', updateLastActivity)
      addActivityListener('keypress', updateLastActivity)
      addActivityListener('scroll', updateLastActivity)
    }

    return authenticated
  } catch (error) {
    console.error('Failed to initialize Keycloak:', error)

    return false
  } finally {
    initPromise = null
  }
}

const setupTokenRefresh = (expiresIn: number) => {
  if (tokenExpirationTimer) {
    clearTimeout(tokenExpirationTimer)
  }

  const refreshTime = (expiresIn - 30) * 1000 // Refresh 30 seconds before expiry
  tokenExpirationTimer = setTimeout(async () => {
    await updateToken()
  }, refreshTime)
}

const updateToken = async (
  minValiditySeconds: number = 60,
  retryCount: number = 3
): Promise<boolean> => {
  if (!keycloakInstance || !keycloakInstance.refreshToken) {
    return false
  }

  if (isSessionIdle()) {
    keycloakInstance.logout()

    return false
  }

  try {
    const refreshed = await keycloakInstance.updateToken(minValiditySeconds)

    if (refreshed) {
      const newExpirationTime = keycloakInstance.tokenParsed?.exp

      if (newExpirationTime) {
        const newExpiresIn = newExpirationTime - Math.floor(Date.now() / 1000)
        setupTokenRefresh(newExpiresIn)
      } else {
        console.error('New token expiration time is not available')
        setupTokenRefresh(300)
      }
    }

    return true
  } catch (error) {
    if (retryCount > 0) {
      console.warn(
        `Token refresh failed, retrying... (${retryCount} attempts left)`
      )

      return updateToken(minValiditySeconds, retryCount - 1)
    }

    console.error('Token refresh failed after multiple attempts', error)

    return false
  }
}
const logout = async () => {
  if (!keycloakInstance) {
    console.error('Keycloak instance is not initialized')

    return
  }

  if (idleCheckInterval) {
    clearInterval(idleCheckInterval)
    idleCheckInterval = null
  }

  removeActivityListeners()
  keycloakInstance.logout({
    redirectUri: window.location.origin + process.env.PUBLIC_URL + '/',
  })
}

export const keycloak = {
  initKeycloak,
  login: () => keycloakInstance?.login(),
  logout: logout,
  getToken: () => keycloakInstance?.token,
  isLoggedIn: () => !!keycloakInstance?.token,
  getKeycloakId: () => keycloakInstance?.tokenParsed?.sub,
  isTokenExpired: () => keycloakInstance?.isTokenExpired(),
  updateToken,
}
